import { UserService } from "@nuskin/ns-account";
import {
  events,
  ConfigService,
  StringService,
  util,
  SitesSessionStorageService,
  StoreFrontSponsorStorageService,
  PersonalOfferStorageService,
  UrlService,
  dateFormatter
} from "@nuskin/ns-util";
import {
  ProductService,
  ShopContextService,
  CartService,
  OrderType,
  Product,
  AdrService,
  FavoritesService,
  PriceType,
  ProductStatus,
  QualificationService
} from "@nuskin/ns-shop";
import webLoyalty from "@nuskin/ns-loyalty-web";
import { retrieveProductionToggles } from "@nuskin/ns-feature-flags";

//const PRODUCT_SMALL_VARIANT = "product-card.selected-variant";
const PENDING_FAVORITE = "pendingFavorite";
const KEY_BREADCRUMB_BACK = "breadcrumbBack";

/**
 * NsProductMixin
 *
 * How to use: This mixin provides all the logic to both NsProductDetail and
 * NsProductCard
 *
 * @event variant-select  When a variant selection occurs, this event emits a
 * function with params: (value, index)
 * @event quantity-select  When a quantity selection occurs, this event emits a
 * function with params: (value, index)
 * @event product-update  When a product updates, this event emits a function
 * with params: (product)
 * @event card-click  When clicking on the ProductCard, this event emits a
 * function with params: (product). If listener is NOT assigned, clicking the
 * card will go to the product detail page
 * @event added-to-cart After product is added to cart, this event emits a
 * function with params: ({sku, qty, product, referrer})
 * @event added-to-adr After product is added to cart, this event emits a
 * function with params: ({sku, qty, product, referrer})
 * @event favorite-click When clicking on Favorite Icon, this event emits a
 * function with params: ({sku, isFavorite})
 */

const NsProductMixin = {
  props: {
    /**
     * Hides ADR Price Always
     */
    hideAdrOverride: {
      type: Boolean,
      default: false
    },
    /**
     * Hides the Variant Selector always
     */
    hideVariantSelector: {
      type: Boolean,
      default: false
    },
    /**
     * Use if the parent needs to control the quantity
     */
    quantity: {
      type: Number,
      default: 1
    },
    /**
     * Product sku
     */
    sku: {
      type: String,
      default: ""
    },
    /**
     * Product-related translations
     */
    translations: {
      type: String,
      default: ""
    },
    /**
     * Loyalty Strings
     */
    loyaltyStrings: {
      type: Object,
      default() {
        return {};
      }
    },
    /**
     * Force product to be a ADR in cart
     */
    adrHasPriority: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      /**
       * Message that will be shown on the exclusive offer ns-message-action component
       */
      activeSalesEvents: [],
      /**
       * Determines the current sku being operated on (could be this.sku or a variant sku)
       */
      activeSku: this.sku,
      /**
       * The price the customer will pay when adding to subscription order
       */
      adrPrice: "",
      /**
       * Base product data
       */
      baseProduct: undefined,
      baseSku: "",
      checkedQualifications: false,
      description: "",
      /**
       * Determines if the "Add To Cart" button is disabled
       */
      disableAddToCart: true,
      /**
       * Determines if the "Add To ADR" button is disabled
       */
      disableAddToAdr: true,
      /**
       * Determines if the "Quantity" dropdown is disabled
       */
      disableQuantitySelector: true,
      eventName: "",
      /**
       * Exclusive offer text determined by user logged in and status.
       */
      exclusiveOfferMessage: "",
      featureFlags: {},
      hideAdr: false,
      imageFallbackSrc: "",
      image: {},
      imageSet: [],
      isBaseSku: false,
      isDistributor: false,
      isExclusive: false,
      /**
       * Determines if the current product is a pending or current favorite
       */
      isFavorite: false,
      isLoggedIn: false,
      isLoyaltyEnabled: false,
      isPreferredCustomer: false,
      isSale: false,
      isStatusSet: false,
      /**
       * Exclusive offer text determined by user logged in and status.
       */
      isVariantSku: false,
      isWholesale: false,
      loadFirstVariant: false,
      /**
       * Translations object
       */
      localTranslations: {
        size: "Size",
        nettoWeight: "Netto Weight",
        addToAdr: "Subscribe To ADR",
        item: "Item",
        adrPriceLabel: "ADR Price",
        description: "Description",
        chooseGift: "Choose Gift",
        adrDisabledText: "Unavailable for ADR",
        wholeSalePriceLabel: "Wholesale Price",
        retailPriceLabel: "Retail Price"
      },
      loyaltyStatusAndLinks: undefined,
      marketAttributes: {},
      /**
       * Market site configuration
       */
      marketConfig: undefined,
      maxPrice: "",
      minPrice: "",
      maxQuantity: 999,
      nettoWeight: "",
      /**
       * The price the customer will pay when adding to cart
       */
      originalPrice: "",
      points: "",
      price: "",
      /**
       * Price label for wholesale/retail when ADR is available
       */
      priceLabel: "",
      /**
       * Active poduct data
       */
      product: undefined,
      /**
       * Determines if Product Status has been handled for the active sku
       */
      productStatusResults: [],
      /**
       * Quantity of product selected
       */
      selectedQuantity: this.quantity,
      referrer: "productMixin",
      salesEventText: "",
      salesLabel: "",
      /**
       * Determines if retail or wholesale price will display as discounted (with strike-through) compared to retail price
       */
      showOriginalCrossedOut: false,
      sizeWeight: "",
      statusMessage: "",
      statusMessageKey: "",
      title: "",
      user: undefined,
      userIsQualified: false,
      userLoyalty: undefined,
      variants: []
    };
  },
  computed: {
    hasContent() {
      if (
        this.product &&
        this.title &&
        (this.image || (this.imageSet && this.imageSet.length > 0))
        // this.isStatusSet?
      ) {
        return true;
      }
      return false;
    },
    showVariantSelector() {
      return !this.hideVariantSelector && (this.isBaseSku || this.isVariantSku);
    },
    showExclusiveOfferMessage() {
      return (
        this.isExclusive && !this.userIsQualified && this.exclusiveOfferMessage
      );
    }
  },
  watch: {
    quantity(newVal) {
      if (newVal !== this.selectedQuantity) {
        this.selectedQuantity = newVal;
      }
    },
    sku: {
      immediate: true,
      handler(newVal) {
        this.resetProductData(newVal);
        this.refreshProductData(newVal);
      }
    },
    hideAdrOverride() {
      this.setHideAdr();
    }
  },
  async mounted() {
    this.setConfig();
    this.setUser();
    await this.setCommonStrings();
    this.setLoyalty();
    this.setHideAdr();
    this.addListeners();
    await this.setFeatureFlags();
  },
  destroyed() {
    this.removeListeners();
  },
  methods: {
    /**
     * Initializes product data
     */
    refreshProductData(sku) {
      ProductService.getProductData(sku)
        .then(productData => {
          let product = new Product({ sku: sku });
          product.setProductData(productData);

          if (product.isVariant()) {
            this.activeSku = sku;
            this.refreshProductData(product.baseSku);
            return;
          } else if (product.isBase()) {
            this.baseSku = sku;
            this.variants = product.getVariantsList();
            this.baseProduct = product;

            if (
              this.activeSku === sku &&
              this.loadFirstVariant &&
              this.variants.length > 0
            ) {
              this.activeSku = this.variants[0].sku;
            }

            for (const variant of this.variants) {
              events.publish(
                events.shop.ADD_SKU_PRODUCT_STATUS_QUEUE,
                variant.sku
              );
            }
          } else {
            this.activeSku = sku;
            this.variants = [];
            this.product = product;
            events.publish(
              events.shop.ADD_SKU_PRODUCT_STATUS_QUEUE,
              this.activeSku
            );
          }

          Object.assign(this.localTranslations, {
            variantType: product.variantType,
            variantDropdownLabel: product.variantDropdownLabel,
            variantDropdownPlaceholder: product.variantDropdownPlaceholder
          });

          this.setActiveProduct();
        })
        .catch(error => {
          this.disable();
          console.error("Failed to retrieve product data.", error);
        });
    },

    setLoyalty() {
      // Check if loyalty is on for market
      if (this.marketConfig.loyalty && this.marketConfig.loyalty.enabled) {
        this.isLoyaltyEnabled = this.marketConfig.loyalty.enabled;

        this.loyaltyStatusAndLinks = this.marketConfig.loyalty;

        if (this.loyaltyStatusAndLinks.enabled) {
          // String Defaults
          this.loyaltyStatusAndLinks.landingPageLinkMessage =
            "Your enJoy Rewards are waiting!";
          this.loyaltyStatusAndLinks.dashboardLinkMessage =
            "enJoy Rewards available!";
          this.loyaltyStatusAndLinks.notOptedIn =
            "Your enJoy Rewards are waiting";
          this.loyaltyStatusAndLinks.productOptedInEarnAndRedeem =
            "{earnPercentage} enJoy Rewards available!";
          this.loyaltyStatusAndLinks.productOptedInEarnNotRedeem =
            "Earn with Nu Skin Rewards";
          this.loyaltyStatusAndLinks.productOptedInRedeemNotEarn =
            "Save with Nu Skin Rewards";

          StringService.getApplicationStrings("loyalty").then(content => {
            this.loyaltyStrings = content;
            // Link message to show to non-opted in users
            if (this.loyaltyStrings.productDisplayNotOptedinMessage) {
              this.loyaltyStatusAndLinks.landingPageLinkMessage = this.loyaltyStrings.productDisplayNotOptedinMessage;
              this.loyaltyStatusAndLinks.notOptedIn = this.loyaltyStrings.productDisplayNotOptedinMessage;
            }
            // Get loyalty information for user
            const loyaltyData = webLoyalty.getLoyaltyRewardsPoints();

            if (loyaltyData && loyaltyData.optInStatus) {
              if (this.loyaltyStrings.productDisplayOptedinMessage) {
                let earnPercentage =
                  loyaltyData.userTier.purchaseRatio * 100 + "%";
                this.loyaltyStatusAndLinks.dashboardLinkMessage = this.loyaltyStrings.productDisplayOptedinMessage.replace(
                  /\{earnPercentage\}/g,
                  earnPercentage
                );
                this.loyaltyStatusAndLinks.productOptedInEarnAndRedeem = this.loyaltyStrings.productDisplayOptedinMessage.replace(
                  /\{earnPercentage\}/g,
                  earnPercentage
                );
              }
              if (this.loyaltyStrings.productOptedInEarnNotRedeem) {
                this.loyaltyStatusAndLinks.productOptedInEarnNotRedeem = this.loyaltyStrings.productOptedInEarnNotRedeem;
              }
              if (this.loyaltyStrings.productOptedInRedeemNotEarn) {
                this.loyaltyStatusAndLinks.productOptedInRedeemNotEarn = this.loyaltyStrings.productOptedInRedeemNotEarn;
              }
              this.loyaltyStatusAndLinks.dashboardPage = this.localeReplace(
                this.loyaltyStatusAndLinks.dashboardPage
              );
              this.userLoyalty = loyaltyData;
            }
          });

          this.loyaltyStatusAndLinks.landingPage = this.localeReplace(
            this.loyaltyStatusAndLinks.dashboardPage
          );
          this.loyaltyStatusAndLinks.siteUrl = this.marketConfig.siteUrl;
        }
      }
    },

    localeReplace(url) {
      let locale = UrlService.getLocale();
      return url.replace(/\{locale\}/g, locale).replace(" ", "");
    },

    /**
     * Sets product to new selection
     */
    setActiveProduct() {
      if (
        this.baseProduct &&
        this.baseProduct.isBase() &&
        this.variants.length > 0
      ) {
        let activeProduct = this.variants.find(
          variantProduct => variantProduct.sku === this.activeSku
        );
        if (!activeProduct) {
          activeProduct = this.baseProduct;
          this.activeSku = activeProduct.sku;
        }
        this.product = activeProduct;
      }

      this.title = this.stripNonWhiteListHTML(
        this.product.getProductSearchTitle()
      );
      this.description = this.product.longDescr;

      this.isBaseSku = this.product.isBase();
      this.isVariantSku = this.product.isVariant();

      this.isExclusive = this.product.isExclusive;
      this.nettoWeight = this.stripNonWhiteListHTML(this.product.nettoWeight);
      this.sizeWeight = this.stripNonWhiteListHTML(this.product.sizeWeight);
      this.eventName = this.product.eventName;
      this.salesEventText = this.product.salesEventText;
      this.marketAttributes = this.product.marketAttributes;

      this.imageSet = (this.product.getProductCarouselImages() || []).map(
        img => ({
          ...img,
          src: img.src.startsWith("/")
            ? `${this.marketConfig.siteUrl}${img.src}`
            : img.src
        })
      );
      const src = (this.product.fullImage
        ? this.product.fullImage
        : this.imageSet.length > 0
        ? this.imageSet[0].src
        : ""
      ).replace(/240.240./g, "");
      this.image = {
        src: src.startsWith("/") ? `${this.marketConfig.siteUrl}${src}` : src,
        alt: this.product.imageAltText || "",
        contentType: "img"
      };

      this.setProductPricingAndAvailability();
      this.$emit("product-update", this.product);
    },

    /**
     * Sets product availability/qualifications (ability to add product to cart) by controlling the maxQuantity
     */
    setProductAvailability() {
      if (this.product) {
        // check if a user is qualified to purchase the product
        CartService.getAddToCartQty(this.product)
          .then(productQuantity => {
            // this.product is volatile here, re-check
            if (this.product) {
              this.maxQuantity = Math.min(
                this.product.maxQuantity,
                this.product.availableQuantity,
                productQuantity
              );
            }
          })
          .catch(error => {
            this.maxQuantity = 0;
            console.error("Failed to retrieve add to cart quantity.", error);
          })
          .finally(() => {
            this.setStatus();
            this.checkedQualifications = true;
          });
      }
    },

    getProp(obj, keys, defaultVal) {
      keys = Array.isArray(keys) ? keys : keys.split(".");
      obj = obj[keys[0]];
      if (obj && keys.length > 1) {
        return this.getProp(obj, keys.slice(1), defaultVal);
      }
      return obj === undefined ? defaultVal : obj;
    },

    /**
     * Sets add to cart/adr and message based on various criteria
     */
    async setStatus() {
      this.statusMessage = "";
      this.statusMessageKey = "";

      if (this.isBaseSku || !this.isStatusSet) {
        this.disable();
        return;
      }

      this.exclusiveOfferMessage = "";
      if (this.isExclusive) {
        if (this.isLoggedIn) {
          const productQualification = await QualificationService.getQualification(
            this.activeSku
          );
          const cartItem = await CartService.getFirstItemBySku(this.activeSku, {
            cartOrderItems: true
          });
          const cartQuantity = (cartItem || {}).qty || 0;

          if (!productQualification) {
            // the event has never been set up or is in the past (post-event/qualification not configured)
            this.exclusiveOfferMessage = this.localTranslations.exclusiveOfferNoQualification;
          } else if (
            (this.userIsQualified && !this.maxQuantity) ||
            (!this.userIsQualified && !productQualification.quantity) ||
            productQualification.quantity === cartQuantity
          ) {
            // qualified to purchase but no quantity available anymore OR not qualified and no qualified quantity left (used qualified quantity)
            this.exclusiveOfferMessage = this.localTranslations.exclusiveOfferUsedUpQty;
          } else if (
            !this.userIsQualified &&
            productQualification.quantity > 0
          ) {
            // not qualified to purchase and quantity is available from qualification (pre-event)
            this.exclusiveOfferMessage = this.localTranslations.exclusiveOfferNotAvailable;
          }
        } else {
          // not logged in to receive exclusive offer
          this.exclusiveOfferMessage = this.localTranslations.exclusiveOfferMessage;
        }
      }

      if (this.product.status === ProductStatus.ReleasedForSale) {
        if (this.maxQuantity > 0) {
          this.enable();
          this.statusMessageKey = "releasedForSale";

          if (
            this.product.backOrderDate &&
            this.localTranslations.backOrderedWithDate
          ) {
            let locale = UrlService.getLocale();
            this.statusMessage = this.localTranslations.backOrderedWithDate.replace(
              "%date%",
              dateFormatter.getCountryFormattedDate(
                this.product.backOrderDate,
                locale
              )
            );
            this.statusMessageKey = "backOrderedWithDate";
          }
        } else {
          this.disable();

          if (!this.isExclusive) {
            if (this.product.availableQuantity > 0) {
              this.statusMessage = this.localTranslations.reachedPurchaseLimit;
              this.statusMessageKey = "reachedPurchaseLimit";
            } else {
              this.statusMessage = this.localTranslations.outOfStock;
              this.statusMessageKey = "outOfStock";
            }
          }
        }
      } else {
        this.disable();

        if (this.product.status === ProductStatus.Discontinued) {
          this.statusMessage = this.localTranslations.discontinued;
          this.statusMessageKey = "discontinued";
        } else if (this.product.status === ProductStatus.NotReleasedForSale) {
          this.statusMessage = this.localTranslations.notReleasedForSale;
          this.statusMessageKey = "notReleasedForSale";
        } else if (this.product.status === ProductStatus.StopStatus) {
          this.statusMessage = this.localTranslations.stopStatus;
          this.statusMessageKey = "stopStatus";
        }
      }
    },

    /**
     * Set product pricing
     */
    setStandardPricing() {
      if (this.product) {
        this.isWholesale =
          this.isDistributor ||
          this.isPreferredCustomer ||
          !!this.marketConfig.showWholeSalePricing;
        if (this.isWholesale) {
          this.product.setPriceAndPvFromType(PriceType.WWHL);
        }

        this.originalPrice = this.product.getPricing(PriceType.WRTL);
        this.price = this.product.getPricing(this.getPriceType());

        this.points = this.product.getPvFixed();

        let adrPriceType = ShopContextService.getPriceType(true, this.user);
        if (this.isWholesale && adrPriceType !== PriceType.WADW) {
          this.adrPrice = this.product.getPricing(PriceType.WADW);
        } else {
          this.adrPrice = this.product.getPricing(adrPriceType);
        }

        this.priceLabel = this.isWholesale
          ? this.localTranslations.wholeSalePriceLabel
          : this.localTranslations.retailPriceLabel;

        this.showOriginalCrossedOut =
          this.isWholesale &&
          !this.isBaseSku &&
          !this.isSale &&
          this.originalPrice > this.price &&
          !!this.marketConfig.showRetailCrossedOut;
      }
    },

    /**
     * Sets sales event pricing if there is an active event for this sku, or normal pricing if not
     */
    setProductPricing() {
      this.product.setPriceAndPvFromType(
        this.getPriceType(),
        this.activeSalesEvents
      );

      this.eventName = this.product.eventName;
      this.salesEventText = this.product.salesEventText;

      if (this.eventName) {
        this.originalPrice = this.product.getOriginalPrice();
        this.price = this.product.getPrice();
        this.salesLabel = this.product.salesLabel || this.salesLabel;
        if (this.user && this.isDistributor) {
          this.points = this.product.getPvFixed();
        }
        this.isSale = true;
        this.showOriginalCrossedOut = true;
      } else {
        this.salesLabel = "";
        this.isSale = false;
        this.setStandardPricing();
      }
    },

    /**
     * Returns price type based on user type
     */
    getPriceType() {
      if (this.isWholesale) {
        return PriceType.WWHL;
      }
      return this.isLoggedIn
        ? ShopContextService.getPriceType(false, this.user)
        : PriceType.WRTL;
    },

    /**
     * Set run config (if config passed), market config, etc.
     */
    setConfig() {
      this.marketConfig = ConfigService.getMarketConfig();

      this.imageFallbackSrc = `${this.marketConfig.siteUrl}/etc/designs/hello/packaged/img/product-basic__fallback-image.svg`;
    },

    setHideAdr() {
      const isPersonalOffer = PersonalOfferStorageService.isPersonalOffer();
      const isStorefront = StoreFrontSponsorStorageService.isStorefront();
      // TODO: remove util.isSkinConsult check and just use util.isSkinConsult() after deploy
      const isSkinConsult = util.isSkinConsult ? util.isSkinConsult() : false;
      const mySiteHideAdr = this.marketConfig.storefront.mySiteHideAdr;
      this.hideAdr =
        !this.adrHasPriority &&
        (this.hideAdrOverride ||
          isPersonalOffer ||
          ((isStorefront || isSkinConsult) && mySiteHideAdr));
    },

    /**
     * Set current user data
     */
    setUser() {
      this.isLoggedIn = UserService.isLoggedIn();
      if (this.isLoggedIn) {
        this.user = UserService.getUser();
        this.isDistributor = this.user.isDistributor();
        this.isPreferredCustomer = this.user.isPreferredCustomer();
      } else {
        this.user = undefined;
        this.isDistributor = false;
        this.isPreferredCustomer = false;
      }
    },

    goToDetails() {
      SitesSessionStorageService.writeItem(KEY_BREADCRUMB_BACK, true);

      if (window.dataLayer && window.ga && window.ga.loaded) {
        let impression = {
          event: events.shop.PRODUCT_IMPRESSION_CLICK,
          ecommerce: {
            click: {
              actionField: {},
              products: [
                {
                  name: this.product.title,
                  id: this.product.sku,
                  price: this.product.price,
                  catalog: window.location.pathname
                    .split("/")
                    .pop()
                    .split(".")[0],
                  position: 1,
                  domain: window.location.hostname
                }
              ]
            }
          },
          eventCallback: () => {
            window.location =
              util.getLocaleUrl() +
              "products/product." +
              this.product.sku +
              ".html";
          }
        };
        window.dataLayer.push(impression);
      } else {
        window.location =
          util.getLocaleUrl() +
          "products/product." +
          this.product.sku +
          ".html";
      }
    },

    handleVariantSelect(variantSku) {
      this.activeSku = variantSku;
      this.setActiveProduct();

      this.$emit("variant-select", variantSku);
    },

    handleQuantitySelect(quantity) {
      this.selectedQuantity = quantity;

      if (+this.selectedQuantity > this.maxQuantity) {
        this.disableAddToCart = true;
        this.disableAddToAdr = true;
      } else if (this.userIsQualified) {
        this.checkAddToCartAndAdr();
      }

      this.$emit("quantity-select", quantity);
    },

    /**
     * Adds product to cart
     */
    handleAddToCart() {
      if (!this.disableAddToCart && this.isStatusSet) {
        window.sessionStorage.setItem("nstoast-allowed", "true");

        let countryCode = CartService.getCartProperty("cntryCd") || "";
        let userId = null;
        if (this.isLoggedIn && this.user) {
          userId = this.user.id;
        }

        let options = {
          sku: this.activeSku,
          product: this.product,
          qty: this.selectedQuantity,
          userId: userId,
          cntryCd: countryCode,
          adr:
            !!this.product.orderTypes[OrderType.ADR] &&
            !this.product.orderTypes[OrderType.ORDER],
          referrer: this.referrer,
          domain: window.location.hostname
        };

        CartService.addProductToCart(options)
          .then(() => {
            this.maxQuantity -= options.qty;
            this.setStatus();
            events.publish(events.shop.ADD_TO_CART, options);
            events.publish(events.shop.SHOW_ADD_TO_BAG, options);
            this.$emit("added-to-cart", options);
          })
          .catch(error => {
            console.error("Failed to add to cart.", error);
          });
      }
    },

    /**
     * Adds product to subscription order
     */
    handleAddToAdr() {
      if (!this.disableAddToAdr && this.isStatusSet) {
        let options = {
          sku: this.activeSku,
          qty: this.selectedQuantity,
          adr: !AdrService.isAdrOverride(),
          product: this.product,
          referrer: this.referrer
        };
        CartService.addProductToCart(options)
          .then(() => {
            this.maxQuantity -= options.qty;
            this.setStatus();
            events.publish(events.shop.ADD_TO_CART, options);
            events.publish(events.shop.SHOW_ADD_TO_BAG, options);
            this.$emit("added-to-adr", options);
          })
          .catch(error => {
            console.error("Failed to add to ADR.", error);
          });
      }
    },
    /**
     * If listener exists, emit it, else go to the product details page
     */
    handleCardClick() {
      if (this.$listeners["card-click"]) {
        this.$emit("card-click", this.product);
      } else {
        this.goToDetails();
      }
    },

    /**
     * Set product is a pending or current favorite
     */
    setIsFavorite() {
      let pending = JSON.parse(localStorage.getItem(PENDING_FAVORITE)) || null;
      if (this.isLoggedIn && pending && pending.sku === this.activeSku) {
        FavoritesService.addToFavorites(this.activeSku).then(() => {
          this.isFavorite = true;
        });
        localStorage.removeItem(PENDING_FAVORITE);
      } else if (this.isLoggedIn) {
        FavoritesService.isInFavorites(this.activeSku).then(data => {
          this.isFavorite = data;
        });
      }
    },

    /**
     * Adds product to favorites, or requests authentication
     */
    handleFavoriteClick() {
      if (this.isLoggedIn) {
        if (this.isFavorite) {
          FavoritesService.removeFromFavorites(this.activeSku);
        } else {
          FavoritesService.addToFavorites(this.activeSku);
        }
        this.isFavorite = !this.isFavorite;
        this.$emit("favorite-click", {
          sku: this.activeSku,
          isFavorite: this.isFavorite
        });
      } else {
        localStorage.setItem(
          PENDING_FAVORITE,
          JSON.stringify({ sku: this.activeSku })
        );
        events.publish(events.authentication.REQUEST_FOR_LOGIN);
      }
    },

    /**
     * Disables input fields
     */
    disable() {
      this.userIsQualified = false;
      this.disableAddToCart = true;
      this.disableAddToAdr = true;
      this.disableQuantitySelector = true;
    },

    /**
     * Enables add and quantity fields depending if order type allows it
     */
    enable() {
      this.userIsQualified = true;
      this.checkAddToCartAndAdr();
      this.disableQuantitySelector =
        this.disableAddToCart && this.disableAddToAdr;
    },

    checkAddToCartAndAdr() {
      const forceAdr = this.adrHasPriority && this.product.orderTypes.adr;
      this.disableAddToCart = forceAdr || !this.product.orderTypes.order;
      this.disableAddToAdr = this.hideAdr || !this.product.orderTypes.adr;
    },

    /**
     * Set price type, pricing, availability, etc.
     */
    setProductPricingAndAvailability() {
      if (this.product) {
        this.product.setPriceAndPvFromType(this.getPriceType());
        this.setProductPricing();
        this.setProductAvailability();
        this.setIsFavorite();
      }
    },

    /**
     * Handle user auth events
     */
    handleUserChange() {
      this.setUser();
      this.setFeatureFlags();
      this.setProductPricingAndAvailability();
    },

    /**
     * Handle product status events
     */
    handleProductStatus(results) {
      if (this.product) {
        results.forEach(async productStatusResult => {
          if (this.isBaseSku || this.isVariantSku) {
            const variant = this.variants.find(
              variantProduct => variantProduct.sku === productStatusResult.SKU
            );
            if (variant) {
              variant.availableQuantity = productStatusResult.availableQuantity;
              variant.addPricingFromStatus(
                productStatusResult,
                this.getPriceType()
              );
              variant.setMarketAttributes(productStatusResult);

              this.productStatusResults.push(productStatusResult);
            }

            if (
              this.isBaseSku &&
              this.variants.every(variantProduct =>
                this.productStatusResults.some(
                  productStatusResult =>
                    productStatusResult.SKU === variantProduct.sku
                )
              )
            ) {
              // We have to use getProp() instead of variantProduct.getPricing
              // because that was returning an array of undefined prices
              const variantPrices = this.variants.map(variantProduct =>
                this.getProp(variantProduct.priceMap, this.getPriceType(), 0)
              );
              this.minPrice = Math.min(...variantPrices).toString();
              this.maxPrice = Math.max(...variantPrices).toString();

              // when loading a base sku first, we set status as true for all variants
              this.isStatusSet = true;
            }
          } else if (this.activeSku === productStatusResult.SKU) {
            this.product.availableQuantity =
              productStatusResult.availableQuantity;
            this.product.addPricingFromStatus(
              productStatusResult,
              this.getPriceType()
            );
            this.product.setMarketAttributes(productStatusResult);

            this.productStatusResults.push(productStatusResult);
          }

          this.setProductPricing();

          if (productStatusResult.marketAttributes) {
            this.marketAttributes = productStatusResult.marketAttributes;
          }

          if (this.product && this.activeSku === productStatusResult.SKU) {
            this.product.setMarketAttributes(productStatusResult);
            this.setProductAvailability();
            this.isStatusSet = true;
          }
        });
      }
    },

    /**
     * Handle product status events
     */
    handleActiveSalesEvents(activeSalesEvents) {
      if (activeSalesEvents && Array.isArray(activeSalesEvents)) {
        this.activeSalesEvents = activeSalesEvents;
      } else {
        this.activeSalesEvents = [];
      }
      this.setProductPricingAndAvailability();
    },

    /**
     * Subscribe to user and product events, should only be called when mounted
     */
    addListeners() {
      // listen to auth (login/logout) changes
      events.subscribe(events.authentication.LOGIN, this.handleUserChange);
      events.subscribe(events.authentication.LOGOUT, this.handleUserChange);

      // get latest qualification status, listen to changes
      events.getValue(
        events.shop.QUALIFICATION_STATUS_CHANGED,
        this.setProductAvailability,
        true // subscribe
      );

      // listen to product status changes
      events.subscribe(
        events.shop.PRODUCT_STATUS_RESULT,
        this.handleProductStatus
      );

      // get latest active sales events, listen to changes
      events.getValue(
        events.salesevent.ACTIVE_EVENTS,
        this.handleActiveSalesEvents,
        true // subscribe
      );

      events.publish(events.salesevent.POLL_MARKET_CONFIG);
    },

    /**
     * Unsubscribe from user and product events, should only be called when destroyed
     */
    removeListeners() {
      // stop listening to auth changes
      events.unsubscribe(events.authentication.LOGIN, this.updateUser);
      events.unsubscribe(events.authentication.LOGOUT, this.updateUser);

      // stop listening to qualification status changes
      events.unsubscribe(
        events.shop.QUALIFICATION_STATUS_CHANGED,
        this.setProductAvailability
      );

      // stop listening to product status changes
      events.unsubscribe(
        events.shop.PRODUCT_STATUS_RESULT,
        this.handleProductStatus
      );

      // stop listening to active sales events changes
      events.unsubscribe(
        events.salesevent.ACTIVE_EVENTS,
        this.handleActiveSalesEvents
      );
    },

    /**
     * Define an array of objects to get PSV labels
     */
    async setCommonStrings() {
      this.localTranslations = {};
      try {
        if (this.translations) {
          const translations =
            this.translations instanceof Object &&
            this.translations.constructor === Object
              ? this.translations
              : JSON.parse(this.translations);
          Object.assign(this.localTranslations, translations);
        }
      } catch (err) {
        console.warn("Unable to parse translation object.", err);
      }
      const commonStringKeys = [
        { key: "addToAdr", dflt: "Add To Adr" },
        { key: "addToCart", dflt: "Add To Cart" },
        { key: "addToFavorites", dflt: "Add To Favorites" },
        { key: "adrLabel", dflt: this.marketConfig.adrProgram },
        {
          key: "backOrderedWithDate",
          dflt: "Your product(s) will ship by: %date%"
        },
        { key: "discontinued", dflt: "Product Discontinued" },
        { key: "exclusiveOfferTitle", dflt: "Exclusive Offer" },
        {
          key: "exclusiveOfferMessage",
          dflt: "Log in to see if you are qualified to purchase this product."
        },
        {
          key: "exclusiveOfferNoQualification",
          dflt: "You do not have access to purchase this product."
        },
        {
          key: "exclusiveOfferUsedUpQty",
          dflt: "You have reached your purchase limit for this product."
        },
        {
          key: "exclusiveOfferNotAvailable",
          dflt:
            "You are qualified to buy this product, but it is not currently available for purchase."
        },
        { key: "item", dflt: "Item" },
        { key: "quantity", dflt: "Quantity" },
        { key: "notReleasedForSale", dflt: "Coming Soon" },
        { key: "outOfStock", dflt: "Out of stock" },
        { key: "psvDist", dflt: "" },
        { key: "psvCust", dflt: "" },
        { key: "psvPfc", dflt: "" },
        { key: "psvAnon", dflt: "" },
        { key: "reachedPurchaseLimit", dflt: "Reached Purchase Limit" },
        { key: "seeDetails", dflt: "See Details" },
        { key: "signIn", dflt: "Sign In" },
        { key: "stopStatus", dflt: "Currently Not Available" },
        // Quantity Selector
        { key: "done", dflt: "Done" },
        { key: "enter", dflt: "Enter" },
        { key: "maxQuantityExceeded", dflt: "Low stock. Only %stock% left." },
        { key: "select", dflt: "Select" }
      ];

      // TODO: This shouldn't be necessary, but translations are failing to
      // load properly (StringService replace error) and this seems to work
      await this.$nextTick();

      try {
        const commonStrings = await StringService.getStrings(commonStringKeys);
        if (commonStrings) {
          if (this.user) {
            switch (this.user.accountType) {
              case "10":
                this.localTranslations.psv = commonStrings.psvDist;
                break;
              case "20":
                this.localTranslations.psv = commonStrings.psvCust;
                break;
              case "30":
                this.localTranslations.psv = commonStrings.psvPfc;
                break;
              default:
                this.localTranslations.psv = commonStrings.psvAnon;
                break;
            }
          } else {
            this.localTranslations.psv = commonStrings.psvAnon;
          }
          Object.assign(this.localTranslations, commonStrings);
        }
      } catch (err) {
        console.warn("Unable to retrieve common translations.", err);
      }
    },

    resetProductData(sku) {
      this.activeSalesEvents = [];
      this.baseProduct = undefined;
      this.baseSku = "";
      this.activeSku = sku;
      this.product = undefined;

      this.adrPrice = "";
      this.description = "";
      this.disableAddToCart = true;
      this.disableAddToAdr = true;
      this.disableQuantitySelector = true;
      this.eventName = "";
      this.exclusiveOfferMessage = "";
      this.hideAdr = false;
      this.imageFallbackSrc = "";
      this.image = {};
      this.imageSet = [];
      this.isBaseSku = false;
      this.isExclusive = false;
      this.isFavorite = false;
      this.isSale = false;
      this.isVariantSku = false;
      this.isWholesale = false;
      this.maxQuantity = 999;
      this.nettoWeight = "";
      this.originalPrice = "";
      this.points = "";
      this.price = "";
      this.priceLabel = "";
      this.selectedQuantity = this.quantity;
      this.salesEventText = "";
      this.salesLabel = "";
      this.showOriginalCrossedOut = false;
      this.sizeWeight = "";
      this.statusMessage = "";
      this.statusMessageKey = "";
      this.title = "";
      this.userIsQualified = false;
      this.variants = [];

      this.checkedQualifications = false;
      this.productStatusResults = [];
      this.isStatusSet = false;
    },

    /**
     * Removes all html not in whitelist
     */
    stripNonWhiteListHTML(text) {
      if (text) {
        let htmlWhiteList = ["sup", "br"];
        htmlWhiteList.forEach(html => {
          text = text.replace(
            new RegExp("<" + html + ">", "g"),
            "%" + html + "%"
          );
          text = text.replace(
            new RegExp("</" + html + ">", "g"),
            "%/" + html + "%"
          );
        });
        text = util.stripHTML(text);
        htmlWhiteList.forEach(html => {
          text = text.replace(
            new RegExp("%" + html + "%", "g"),
            "<" + html + ">"
          );
          text = text.replace(
            new RegExp("%/" + html + "%", "g"),
            "</" + html + ">"
          );
        });
      }
      return text || "";
    },

    /**
     * Sets the feature flags
     */
    async setFeatureFlags() {
      let userId;
      if (UserService.isLoggedIn()) {
        userId = UserService.getUser().id;
      }
      const flags = await retrieveProductionToggles(userId);
      this.featureFlags = Array.from(flags).reduce(
        (a, x) => Object.assign(a, { [x]: true }),
        {}
      );
    }
  }
};

export default NsProductMixin;
